import { Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { memo, PropsWithChildren, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DeliveryDTO } from '../../api/generated';
import { DefaultErrorScreen, DefaultLoadingScreen } from '../ApiLoadingGuard';
import { ApiError } from '../ApiLoadingGuard/ApiLoadingGuard';
import { extractDeliveryEntries } from './deliveryConfig';

export interface DeliveryNoteProps {
  delivery: DeliveryDTO;
  capturer_id?: string;
}

export interface DeliveryNoteGuardedProps extends Omit<DeliveryNoteProps, 'delivery'> {
  delivery: UseQueryResult<DeliveryDTO, ApiError>;
}

export function DeliveryNoteGuarded({ delivery, capturer_id }: DeliveryNoteGuardedProps): ReactElement {
  const { isPending, isError, data, error } = delivery;

  if (isError) {
    return (
      <DefaultErrorScreen
        textOverride={[{ status: 404, text: 'error.predictionNotAvailable' }]}
        iconSize='medium'
        errors={[error]}
      />
    );
  }
  if (isPending) {
    return <DefaultLoadingScreen />;
  }

  return <DeliveryNote delivery={data} capturer_id={capturer_id} />;
}

export const DeliveryNote = memo(function ({ delivery, capturer_id }: DeliveryNoteProps): ReactElement {
  const { t } = useTranslation();

  const deliveryEntries = useMemo(() => {
    return extractDeliveryEntries(delivery, capturer_id);
  }, [delivery, capturer_id]);

  return (
    <TableContainer
      sx={{
        overflowY: 'auto',
        paddingLeft: 2,
        pb: 2,
        overflowX: 'hidden',
        boxSizing: 'border-box',
        height: '100%',
      }}
    >
      <Table
        size='small'
        aria-label='delivery details table'
        sx={{
          tableLayout: 'fixed',
          width: 'calc(100% - 2rem)',
        }}
      >
        <TableBody
          sx={{
            '& .MuiTableCell-root': {
              wordBreak: 'break-word',
              verticalAlign: 'top',
            },
          }}
        >
          {deliveryEntries.map(({ key, label, value }) => (
            <TableRow key={key} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell component='th' scope='row' sx={{ fontWeight: 'medium', width: '40%' }}>
                <Typography noWrap>{t(label)}</Typography>
              </TableCell>
              <TableCell align='left' sx={{ width: '60%' }}>
                <Typography>{value ?? 'N/A'}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

export const DeliveryNoteContainer = memo(function ({ children }: PropsWithChildren): ReactElement {
  return (
    <Paper
      elevation={8}
      sx={{
        borderRadius: '10px',
        height: '100%',
        minHeight: '10vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {children}
    </Paper>
  );
});

export const DeliveryNoteTitleLayout = memo(function ({ children }: PropsWithChildren): ReactElement {
  return (
    <Typography variant='h6' paddingLeft={2} paddingTop={2} gutterBottom>
      {children}
    </Typography>
  );
});
